import React, { useMemo } from 'react';
import {
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Spacer,
  Tag,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { CheckIcon, ChevronDownIcon } from '@chakra-ui/icons';
import type { SavedSelection } from '../savedSelectionsStorage';

export interface SavedSelectionsMenuProps {
  savedSelections: SavedSelection[];
  currentIds: Set<string>;
  selectionKind: 'default' | 'all' | 'none' | 'ids';
  canSave: boolean;
  onLoad: (selection: SavedSelection) => void;
  onOpenSave: () => void;
  onOpenManage: () => void;
}

const SavedSelectionsMenu: React.FC<SavedSelectionsMenuProps> = ({
  savedSelections,
  currentIds,
  selectionKind,
  canSave,
  onLoad,
  onOpenSave,
  onOpenManage,
}) => {
  const activeId = useMemo<string | null>(() => {
    if (selectionKind !== 'ids') return null;
    for (const s of savedSelections) {
      if (s.companyIds.length !== currentIds.size) continue;
      const all = s.companyIds.every((id) => currentIds.has(id));
      if (all) return s.id;
    }
    return null;
  }, [savedSelections, currentIds, selectionKind]);

  const activeName = useMemo<string | null>(() => {
    if (activeId === null) return null;
    return savedSelections.find((s) => s.id === activeId)?.name ?? null;
  }, [savedSelections, activeId]);

  const saveItem = (
    <MenuItem
      isDisabled={!canSave}
      onClick={canSave ? onOpenSave : undefined}
    >
      Save current…
    </MenuItem>
  );

  return (
    <Menu placement="bottom-start" autoSelect={false}>
      <MenuButton
        as={Button}
        size="xs"
        variant="outline"
        rightIcon={<ChevronDownIcon />}
      >
        {activeName ?? 'Saved selections'}
      </MenuButton>
      <MenuList minW="220px" maxW="320px">
        {savedSelections.length === 0 ? (
          <Text px={3} py={2} fontSize="sm" color="gray.500">
            No saved selections yet.
          </Text>
        ) : (
          savedSelections.map((s) => {
            const isActive = s.id === activeId;
            return (
              <MenuItem key={s.id} onClick={() => onLoad(s)}>
                <HStack w="100%" spacing={2}>
                  {isActive ? (
                    <CheckIcon color="blue.500" boxSize="0.75em" />
                  ) : (
                    <CheckIcon visibility="hidden" boxSize="0.75em" />
                  )}
                  <Text
                    flex="1"
                    isTruncated
                    fontWeight={isActive ? 'semibold' : 'normal'}
                  >
                    {s.name}
                  </Text>
                  <Spacer />
                  <Tag size="sm" variant="subtle">
                    {s.companyIds.length.toLocaleString()}
                  </Tag>
                </HStack>
              </MenuItem>
            );
          })
        )}
        <MenuDivider />
        {canSave ? (
          saveItem
        ) : (
          <Tooltip
            label="Select at least one customer first."
            placement="left"
            hasArrow
          >
            {/* Tooltip needs a focusable child; MenuItem with isDisabled still renders. */}
            <span>{saveItem}</span>
          </Tooltip>
        )}
        {savedSelections.length >= 1 && (
          <>
            <MenuDivider />
            <MenuItem onClick={onOpenManage}>Manage…</MenuItem>
          </>
        )}
      </MenuList>
    </Menu>
  );
};

export default SavedSelectionsMenu;
